import * as React from "react";
import { Component } from "react";
import Sentence from "../../component/nlp/Sentence";
import GlmId from "../../component/GlmId";
import TypedReportApi, { TypedReport } from "../../api/TypedReportApi";
import { AuthManager } from "../../component/auth/AuthContext";
import SSEButton, { ActionEvent } from "../../component/SSEButton";
import FileChooser from "../../component/filechooser/FileChooser";
import GlmQuery from "../../component/GlmQuery";
import { Query, QueryMode } from "../../api/Query";
import i18n from "i18next";
import { Page } from "../../component/Pagination";

interface ReportListProps {
  authManager: AuthManager;
}

interface ReportListState {
  loading: boolean;
  query: Query;
  page: Page;
  items: Array<TypedReport>;
  processPercent: number;
  file?: File;
}

export default class ReportList extends Component<ReportListProps, ReportListState> {
  private typedReportApi: TypedReportApi;

  constructor(props: ReportListProps) {
    super(props);

    this.typedReportApi = TypedReportApi.with(props.authManager.cancelableClient());

    this.state = {
      loading: false,
      query: { mode: QueryMode.GLM, q: "" },
      page: Page.default(),
      items: [],
      processPercent: 0,
    };
  }

  componentDidMount() {
    if (this.state.page.current < 0) this.moreResults();
  }

  componentWillUnmount() {
    this.typedReportApi.cancel();
  }

  moreResults() {
    this.setState({ loading: true }, () => {
      this.typedReportApi.search(this.state.page.nextPage(), this.state.query).then((searchResult) => {
        this.setState((prevState) => {
          const page = prevState.page;
          page.totalItems = searchResult.total;
          return {
            page: page,
            loading: false,
            items: prevState.items.concat(searchResult.resultItems),
          };
        });
      });
    });
  }

  private onKeyDown(evt: React.KeyboardEvent<HTMLFormElement>) {
    // map CTRL + enter
    if ((evt.keyCode == 10 || evt.keyCode == 13) && evt.ctrlKey) {
      this.search(evt);
    }
  }

  search(evt?: React.FormEvent<HTMLFormElement>) {
    if (evt) evt.preventDefault();
    this.setState({ page: this.state.page.reset(), items: [] }, () => this.moreResults());
  }

  startReportImport(): Promise<ActionEvent> {
    if (this.state.file) {
      return this.typedReportApi.processImport(this.state.file);
    }
  }

  onQueryChange(query: Query) {
    this.setState({ query: query });
  }

  render() {
    return (
      <>
        <form
          onSubmit={(evt) => this.search(evt)}
          className="container-fluid m-2"
          onKeyDown={(evt) => this.onKeyDown(evt)}
        >
          <div className="row form-group">
            <div className="col-md-9">
              <div className="input-group mb-3">
                <GlmQuery onChange={(query) => this.onQueryChange(query)} query={this.state.query} />

                <div className="input-group-append">
                  <button type="submit" className="btn btn-primary">
                    <i className="fa fa-search" />
                  </button>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <a
                className="btn btn-secondary"
                href={"/api/typed-reports/csv?q=" + this.state.query.q + "&mode=" + this.state.query.mode}
                download
              >
                <i className="fa fa-download" />
              </a>

              <div className="btn-group ml-2" role="group" aria-label="Importer des CR">
                <FileChooser className="btn btn-secondary" onFile={(file) => this.setState({ file })} accept=".csv">
                  {this.state.file ? this.state.file.name : i18n.t("global.import")}
                </FileChooser>

                {this.state.file && (
                  <SSEButton
                    className="btn btn-secondary"
                    start={() => this.startReportImport()}
                    onFinish={() => this.search()}
                  >
                    {i18n.t("global.throw")}
                  </SSEButton>
                )}
              </div>
            </div>
          </div>
        </form>

        <div className="m-2">
          {i18n.t("global.total")} : {this.state.page.totalItems}
        </div>

        <table className="table table-striped table-dark">
          <thead>
            <tr>
              <th>GlmStudyId</th>
              <th>{i18n.t("global.report")}</th>
            </tr>
          </thead>
          <tbody>
            {this.state.items.map((item) => (
              <tr key={item.id}>
                <td>
                  <p>
                    <GlmId value={item.glmStudyId} />
                  </p>

                  {item.tags.map((tag) => (
                    <span key={tag} className="badge badge-dark">
                      {tag}
                    </span>
                  ))}
                </td>
                <td>
                  <div className="container-fluid">
                    {item.sentences &&
                      item.sentences.map((sentence, si) => {
                        return (
                          <div key={si} className="row">
                            <div className="col-md-11 col-auto">
                              <Sentence value={sentence} />
                            </div>

                            <div className="col-md-1">
                              {sentence.tags.map((tag, ti) => (
                                <span key={ti} className="badge badge-dark">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="text-center">
          {this.state.loading ? (
            <div>
              <i className="fa fa-spinner fa-spin"></i>
            </div>
          ) : (
            <button className="btn" onClick={this.moreResults.bind(this)}>
              {i18n.t("global.moreResults")}
            </button>
          )}
        </div>
      </>
    );
  }
}
